import cx from "classnames";
import React, { type FC, useEffect, useMemo, useRef } from "react";
import { useEffectOnce } from "react-use";
import * as rx from "rxjs";

import { type Result } from "../../easel/runtime";

//
// IMPORTANT AND STRANGE NOTE:
//
// Do *NOT* use `isFulfilled` here. Merely importing it seems to result in some cells crashing. We
// don't know why.
//
// WHY WE KNOW THIS:
//
//   * We pulled everything out of this function, leaving only `isFulfilled`. We did *NOT* call it,
//     we simply reference it.
//   * A test canvas that contains only a cell with an `InputSelectAlpha` in it.
//   * It crashes, leaving an error about calling removeChild on a node that is not the parent.
//
// IF YOU CHANGE THIS:
//
//   * Please do extensive QA to make sure it's not crashing.
//

function indicatorShouldBePending(result?: Result) {
	return result?.type === "PENDING";
}

export const CellUpdateIndicator: FC<{ result?: Result }> = ({ result }) => {
	const indicatorRef = useRef<HTMLDivElement>(null);
	const [subject, subscription] = useMemo(() => {
		const subject = new rx.Subject<Result | undefined>();
		const subscription = subject
			.pipe(
				rx.flatMap((v) => {
					if (v?.type !== "FULFILLED" || indicatorShouldBePending(v)) {
						return [];
					}

					// IMPORTANT: Manually swap out the classes; React state empirically is a little
					// sluggish.
					indicatorRef.current?.classList.remove(
						"transition-opacity",
						"opacity-0",
						"duration-500"
					);
					indicatorRef.current?.classList.add("opacity-100");
					return [v];
				}),
				rx.debounce(() => rx.timer(100))
			)
			.subscribe(() => {
				//
				// After a short delay, fade the indicator out. This is what gives it the "flaring"
				// effect.
				//

				// IMPORTANT: Manually swap out the classes; React state empirically is a little
				// sluggish.
				indicatorRef.current?.classList.remove("opacity-100");
				indicatorRef.current?.classList.add(
					"transition-opacity",
					"opacity-0",
					"duration-500"
				);
			});

		return [subject, subscription] as const;
	}, []);

	useEffectOnce(() => {
		return () => subscription.unsubscribe();
	});

	useEffect(() => {
		subject.next(result);
	}, [subject, result]);

	return (
		<div
			ref={indicatorRef}
			className={cx(
				"z-cell",
				// NOTE: These must be inline, or the Tailwind JIT compiler will remove them.
				{
					"bg-negative opacity-100": result?.type === "REJECTED" || result?.staticError,
					"bg-quaternary opacity-100":
						!(result?.type === "REJECTED" || result?.staticError) &&
						indicatorShouldBePending(result),
					"bg-positive opacity-0 transition-opacity duration-500":
						!(result?.type === "REJECTED" || result?.staticError) &&
						!indicatorShouldBePending(result),
				}
			)}
			style={{ width: "3px" }}
		/>
	);
};
